import React from 'react';
import { Phone, Calendar, Clock, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const PHONE = '+33674626368';
const WHATSAPP_URL = 'https://wa.me/33674626368?text=Bonjour%2C%20je%20souhaite%20r%C3%A9server%20un%20taxi%20Yellow%20Cab%20Corsica.';
const HERO_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/79680a12-b72c-4e4d-9800-308b06c8b20c/images/70144de55176f384e12e20ea02315f7c325dea8b78f130704416437d28c58002.png';

const ANIM_BADGE    = { initial: { opacity: 0, y: -10 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.6 } };
const ANIM_TITLE    = { initial: { opacity: 0, y: 20 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.1 } };
const ANIM_SUBTITLE = { initial: { opacity: 0, y: 20 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.2 } };
const ANIM_BUTTONS  = { initial: { opacity: 0, y: 20 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.3 } };

const Hero = () => {
  const { t } = useTranslation();
  const scrollToReservation = () => {
    document.getElementById('reservation')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
      style={{ backgroundImage: `url(${HERO_IMAGE})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>

      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0A0A0A] to-transparent" />

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        <motion.div
          {...ANIM_BADGE}
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 bg-[#EAB308]/15 border border-[#EAB308]/30 rounded-full px-4 py-1.5 mb-6">
          <Clock size={13} className="text-[#EAB308]" />
          <span className="text-[#EAB308] text-xs font-semibold tracking-[0.15em] uppercase">{t('hero.badge')}</span>
        </motion.div>

        <motion.h1
          {...ANIM_TITLE}
          data-testid="hero-title"
          className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white mb-6 leading-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('hero.title_1')}<br />
          <span className="font-semibold">{t('hero.title_2')}</span><br />
          <span className="text-[#EAB308]">{t('hero.title_3')}</span>
        </motion.h1>

        <motion.p
          {...ANIM_SUBTITLE}
          data-testid="hero-subtitle"
          className="text-base sm:text-lg text-white/75 max-w-2xl mx-auto mb-10 leading-relaxed">
          {t('hero.subtitle')}{' '}
          <br className="hidden sm:block" />
          {t('hero.subtitle_2')}
        </motion.p>

        <motion.div
          {...ANIM_BUTTONS}
          className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
          <a href={`tel:${PHONE}`} data-testid="hero-call-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#EAB308] hover:bg-[#CA8A04] text-black font-semibold px-8 py-4 rounded-full text-base transition-colors duration-300 shadow-lg shadow-yellow-500/20">
            <Phone size={18} />{t('hero.call')}
          </a>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
            className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-8 py-4 rounded-full text-base transition-colors duration-300 shadow-lg shadow-green-500/20">
            <MessageCircle size={18} />{t('hero.whatsapp')}
          </a>
          <button onClick={scrollToReservation} data-testid="hero-reserve-btn"
            className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 text-white font-semibold px-8 py-4 rounded-full text-base transition-all duration-300">
            <Calendar size={18} />{t('hero.reserve')}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
